// Use environment variable with fallback for API base URL

// Get base domain from environment variable
export const BASE_DOMAIN = window.env?.BASE_DOMAIN || 'localhost:3000';

// Use BASE_DOMAIN for API calls with proper protocol and port
export const API_BASE_URL = (() => {
  // Use BASE_DOMAIN for API calls with proper protocol and port
  const baseDomain = (window.env?.BASE_DOMAIN || 'localhost:3000').replace(/:\d+$/, ''); // Remove port if present

  // Use https for production domain, http for localhost
  const protocol = baseDomain === 'localhost' ? 'http' : 'https';
  return `${protocol}://${baseDomain}:8090`;
})();

// Function to detect if we're on the JIT Portal subdomain
export const isJITPortal = () => {
  const hostname = window.location.hostname;
  const baseDomain = BASE_DOMAIN.replace(/:\d+$/, ''); // Remove port if present

  // Check for jit.{baseDomain}
  if (hostname === `jit.${baseDomain}`) {
    return true;
  }

  // For localhost development on port 3001
  if (hostname === 'localhost' && window.location.port === '3001') {
    return true;
  }

  return false;
};

// Function to detect if we're on the CloudKeeper Org Services portal path.
// Accepts the new /services prefix plus two legacy prefixes (/security, /cloudtrail)
// so bookmarks to the old URLs continue to route into the portal app.
export const isOrgServicesPortal = () => {
  const path = window.location.pathname;
  return path.startsWith('/services') || path.startsWith('/security') || path.startsWith('/cloudtrail');
};

// Function to detect if we're on a customer subdomain
export const isCustomerSubdomain = () => {
  const hostname = window.location.hostname;

  // For localhost development
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return false;
  }

  // Get base domain from environment
  const baseDomain = BASE_DOMAIN.replace(/:\d+$/, ''); // Remove port if present

  // Check if current hostname is exactly the base domain
  if (hostname === baseDomain) {
    return false; // We're on the main domain
  }

  // Check if current hostname ends with the base domain and has a subdomain
  if (hostname.endsWith('.' + baseDomain)) {
    const subdomain = hostname.replace('.' + baseDomain, '');
    // Make sure it's not www or other common non-customer subdomains
    return subdomain !== 'www' && subdomain !== 'api' && subdomain !== 'admin' && subdomain !== 'docs';
  }

  return false;
};

// Function to get customer ID from subdomain
export const getCustomerIdFromSubdomain = () => {
  if (!isCustomerSubdomain()) return null;

  const hostname = window.location.hostname;
  const baseDomain = BASE_DOMAIN.replace(/:\d+$/, ''); // Remove port if present

  // Extract subdomain
  if (hostname.endsWith('.' + baseDomain)) {
    return hostname.replace('.' + baseDomain, '');
  }

  return null;
};

// Function to get domain for Keycloak and other services
export const getServiceDomain = () => {
  // Use the same domain as the frontend for consistency
  const baseDomain = BASE_DOMAIN.replace(/:\d+$/, ''); // Remove port if present

  // For localhost, return localhost
  if (baseDomain === 'localhost' || baseDomain.includes('localhost')) {
    return 'localhost';
  }

  // For production domains, return the base domain
  return baseDomain;
};

// Function to get Keycloak domain (different from service domain)
export const getKeycloakDomain = () => {
  // Check if there's a specific Keycloak domain in env
  const keycloakDomain = window.env?.KEYCLOAK_DOMAIN;
  if (keycloakDomain) {
    return keycloakDomain;
  }

  // Fallback to base domain logic
  const baseDomain = BASE_DOMAIN.replace(/:\d+$/, ''); // Remove port if present

  // For localhost, return localhost:8443
  if (baseDomain === 'localhost' || baseDomain.includes('localhost')) {
    return 'localhost:8443';
  }

  // For production domains, prepend 'login.' to the base domain
  return `login.${baseDomain}`;
};

// Function to get full Keycloak URL with protocol
export const getKeycloakURL = () => {
  const keycloakDomain = getKeycloakDomain();

  // Use http for localhost, https for production
  const protocol = keycloakDomain.includes('localhost') ? 'http' : 'https';

  return `${protocol}://${keycloakDomain}`;
};

// AWS SAML Login URL
export const getAWSLoginURL = () => {
  const keycloakURL = getKeycloakURL();

  // Get customer's realm dynamically
  const customerId = getCustomerIdFromSubdomain();
  let realm = 'aws-sso'; // Default realm for non-customer contexts

  // If we're on a customer subdomain, use their specific realm
  if (customerId) {
    realm = customerId;
  }

  return `${keycloakURL}/realms/${realm}/protocol/saml/clients/aws-local`;
};

// Keycloak Admin SAML Login URL
export const getKeycloakAdminLoginURL = () => {
  const keycloakURL = getKeycloakURL();
  const callbackUrl = encodeURIComponent(`${window.location.origin}/sso-callback`);

  // Get customer's realm dynamically
  const customerId = getCustomerIdFromSubdomain();
  let realm = 'aws-sso'; // Default realm for non-customer contexts

  // If we're on a customer subdomain, use their specific realm
  if (customerId) {
    realm = customerId;
  }

  return `${keycloakURL}/realms/${realm}/protocol/saml/clients/ckauth-admin?redirect=${callbackUrl}`;
};

// CK Auth Application SAML Login URL (for customer subdomain root redirect)
export const getCkAuthApplicationURL = () => {
  const keycloakURL = getKeycloakURL();

  // Get customer's realm dynamically
  const customerId = getCustomerIdFromSubdomain();
  if (!customerId) {
    return null; // Only works on customer subdomain
  }

  return `${keycloakURL}/realms/${customerId}/protocol/saml/clients/ckauth-applications`;
};

// Helper: Get customerId from subdomain or null
export const getCustomerId = () => getCustomerIdFromSubdomain();

// Helper: Get users endpoint (customer-specific if customerId exists)
export const getUsersEndpoint = () => {
  const customerId = getCustomerId();
  return customerId
    ? `/api/v1/customers/${customerId}/users`
    : '/api/v1/users';
};

// Helper: Get groups endpoint (customer-specific if customerId exists)
export const getGroupsEndpoint = () => {
  const customerId = getCustomerId();
  return customerId
    ? `/api/v1/customers/${customerId}/groups`
    : '/api/v1/groups';
};

// Helper: Get group members endpoint
export const getGroupMembersEndpoint = (groupName) => {
  const customerId = getCustomerId();
  const encodedGroupName = encodeURIComponent(groupName);
  return customerId
    ? `/api/v1/customers/${customerId}/groups/${encodedGroupName}/members`
    : `/api/v1/groups/${encodedGroupName}/members`;
};

// Helper: Get identity providers endpoint (customer-specific if customerId exists)
export const getIdentityProvidersEndpoint = () => {
  const customerId = getCustomerId();
  return customerId
    ? `/api/v1/customers/${customerId}/identity-providers`
    : '/api/v1/identity-providers';
};

// Helper: Get Google IdP endpoint
export const getGoogleIdPEndpoint = () => {
  return `${getIdentityProvidersEndpoint()}/google`;
};

// Helper: Get IdP status endpoint
export const getIdPStatusEndpoint = () => {
  return `${getIdentityProvidersEndpoint()}/status`;
};

// Helper: Get Google IdP validation endpoint
export const getGoogleIdPValidationEndpoint = () => {
  return `${getGoogleIdPEndpoint()}/validate`;
};

// Helper: Generate a URL-safe alias from display name (mirrors backend logic)
//
// Mirrors services.GenerateURLSafeAlias (backend/services/idp_utils.go:14). The
// `providerType` argument is free-form, so `generatePreviewAlias(name, 'saml')`
// works without any change here.
//
// One verified divergence from the Go implementation: for an EMPTY display name
// this returns the bare `providerType` ("saml") while Go returns
// `providerType + "-"` ("saml-"). Harmless in practice because displayName is a
// required field in every IdP dialog, so the empty branch is unreachable from
// the UI. The truncate-then-trim-trailing-hyphen order already matches Go
// (idp_utils.go:29-32 vs :243-245 below), which is the case that actually
// affects a real alias.
//
// Whatever this returns is only ever a PREVIEW: the backend mints the real alias
// and GenerateUniqueAlias appends -1/-2 on collision.
export const generatePreviewAlias = (displayName, providerType = 'google') => {
  if (!displayName || !displayName.trim()) {
    return providerType;
  }

  // Convert to lowercase and replace non-alphanumeric chars with hyphens
  let alias = displayName.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  // Trim leading/trailing hyphens
  alias = alias.replace(/^-+|-+$/g, '');

  // Prepend provider type prefix
  alias = `${providerType}-${alias}`;

  // Limit length to 64 chars
  if (alias.length > 64) {
    alias = alias.substring(0, 64).replace(/-+$/, '');
  }

  return alias;
};

// Helper: Get Google IdP redirect URI for OAuth configuration
export const getGoogleIdPRedirectURI = (alias = null) => {
  // Get customer ID to determine realm
  const customerId = getCustomerIdFromSubdomain();
  const realm = customerId || 'aws-sso';

  const keycloakURL = getKeycloakURL();

  // Use provided alias or default to 'google' for backward compatibility
  const idpAlias = alias || 'google';

  return `${keycloakURL}/realms/${realm}/broker/${idpAlias}/endpoint`;
};

// Helper: Get Microsoft IdP endpoint
export const getMicrosoftIdPEndpoint = () => {
  return `${getIdentityProvidersEndpoint()}/microsoft`;
};

// Helper: Get Microsoft IdP validation endpoint
export const getMicrosoftIdPValidationEndpoint = () => {
  return `${getMicrosoftIdPEndpoint()}/validate`;
};

// Helper: Get Microsoft IdP redirect URI for OAuth configuration
export const getMicrosoftIdPRedirectURI = (alias = null) => {
  // Get customer ID to determine realm
  const customerId = getCustomerIdFromSubdomain();
  const realm = customerId || 'aws-sso';

  const keycloakURL = getKeycloakURL();

  // Use provided alias or default to 'microsoft' for backward compatibility
  const idpAlias = alias || 'microsoft';

  return `${keycloakURL}/realms/${realm}/broker/${idpAlias}/endpoint`;
};

// Custom IdP endpoint helpers
export const getCustomIdPEndpoint = () => {
  const customerId = getCustomerId();
  return customerId
    ? `/api/v1/customers/${customerId}/identity-providers/custom`
    : '/api/v1/identity-providers/custom';
};

export const getCustomIdPValidationEndpoint = () => {
  return `${getCustomIdPEndpoint()}/validate`;
};

// Helper: Get Custom IdP redirect URI for OAuth configuration
export const getCustomIdPRedirectURI = (alias = null) => {
  // Get customer ID to determine realm
  const customerId = getCustomerIdFromSubdomain();
  const realm = customerId || 'aws-sso';

  const keycloakURL = getKeycloakURL();

  // Use provided alias or default to 'custom' for backward compatibility
  const idpAlias = alias || 'custom';

  return `${keycloakURL}/realms/${realm}/broker/${idpAlias}/endpoint`;
};

// SAML 2.0 IdP endpoint helpers
//
// In SAML terms Keycloak is the Service Provider and the customer's IdP
// (Entra ID, Okta, Google Workspace, ADFS) is the asserting party. The three
// getSamlSP* helpers below build the values the admin must paste INTO their IdP.
export const getSamlIdPEndpoint = () => {
  const customerId = getCustomerId();
  return customerId
    ? `/api/v1/customers/${customerId}/identity-providers/saml`
    : '/api/v1/identity-providers/saml';
};

// Helper: Get SAML metadata import endpoint (accepts a metadata URL or raw XML)
export const getSamlIdPImportEndpoint = () => {
  return `${getSamlIdPEndpoint()}/import-metadata`;
};

// Helper: Get SAML Service Provider info endpoint (authoritative SP values +
// the server-computed alias, before the provider exists)
export const getSamlIdPSPInfoEndpoint = () => {
  return `${getSamlIdPEndpoint()}/sp-info`;
};

// Helper: Get the SAML ACS / Reply URL for a given IdP alias.
// Client-side fallback only -- prefer idp.serviceProvider.acsUrl from the API,
// which is built from the backend's KEYCLOAK_DOMAIN and is authoritative.
export const getSamlACSURL = (alias = null) => {
  // Get customer ID to determine realm
  const customerId = getCustomerIdFromSubdomain();
  const realm = customerId || 'aws-sso';

  const keycloakURL = getKeycloakURL();

  // Use provided alias or default to 'saml' for backward compatibility
  const idpAlias = alias || 'saml';

  return `${keycloakURL}/realms/${realm}/broker/${idpAlias}/endpoint`;
};

// Helper: Get the SAML SP Entity ID (Entra calls this the "Identifier").
// Realm-scoped, so it is the same value for every SAML IdP in the realm.
export const getSamlSPEntityID = () => {
  // Get customer ID to determine realm
  const customerId = getCustomerIdFromSubdomain();
  const realm = customerId || 'aws-sso';

  const keycloakURL = getKeycloakURL();

  return `${keycloakURL}/realms/${realm}`;
};

// Helper: Get the public SP metadata descriptor URL for a given IdP alias.
// Unauthenticated, and 404s until the identity provider actually exists.
export const getSamlSPDescriptorURL = (alias = null) => {
  // Get customer ID to determine realm
  const customerId = getCustomerIdFromSubdomain();
  const realm = customerId || 'aws-sso';

  const keycloakURL = getKeycloakURL();

  // Use provided alias or default to 'saml' for backward compatibility
  const idpAlias = alias || 'saml';

  return `${keycloakURL}/realms/${realm}/broker/${idpAlias}/endpoint/descriptor`;
};

// Helper: Get SCIM base URL
export const getSCIMBaseURL = () => {
  const customerId = getCustomerIdFromSubdomain();
  const realm = customerId || 'aws-sso';

  // Get Keycloak domain and replace "login" with "scim"
  const keycloakDomain = getKeycloakDomain();

  // For localhost, use localhost directly
  if (keycloakDomain.includes('localhost')) {
    return `http://localhost:8070/scim/v2`;
  }

  // For production, remove "login." prefix to get base domain
  const scimDomain = keycloakDomain.replace(/^login\./, '');

  return `https://${realm}.${scimDomain}:8070/scim/v2`;
};

export const API_ENDPOINTS = {
  HEALTH: '/health',

  // Application Admin endpoints (no subdomain)
  APP_ADMIN_LOGIN: '/api/v1/app-admin/login',
  APP_ADMIN_STATUS: '/api/v1/app-admin/status',
  APP_ADMIN_INIT: '/api/v1/app-admin/init',
  APP_ADMIN_ME: '/api/v1/app-admin/me',

  // Legacy auth endpoint (still used for app admin token validation)
  AUTH_ME: '/api/v1/auth/me',

  // Application Admin MFA endpoints
  APP_ADMIN_MFA_STATUS: '/api/v1/app-admin/mfa/status',
  APP_ADMIN_MFA_SETUP: '/api/v1/app-admin/mfa/setup',
  APP_ADMIN_MFA_DISABLE: '/api/v1/app-admin/mfa/disable',

  // Customer management endpoints
  CUSTOMERS: '/api/v1/customers',
  CUSTOMER_DETAIL: '/api/v1/customers/{customerId}',
  CUSTOMER_UPDATE: '/api/v1/customers/{customerId}',
  CUSTOMER_DELETE: '/api/v1/customers/{customerId}',
  CUSTOMER_RESOURCE_COUNTS: '/api/v1/customers/{customerId}/resource-counts',

  // Customer AWS Account deboard endpoints
  CUSTOMER_AWS_ACCOUNTS_DEBOARD: '/api/v1/customers/{customerId}/aws-accounts/{accountId}/deboard',

  // Customer AWS Account assignment check endpoints
  CUSTOMER_AWS_ACCOUNTS_CHECK_ASSIGNMENTS: '/api/v1/customers/{customerId}/permission-set-assignments',

  // Customer-specific init endpoints
  CUSTOMER_INIT: '/api/v1/customers/{customerId}/init',

  // SSO callback
  SSO_CALLBACK: '/sso-callback',

  // Customer-specific auth endpoints
  CUSTOMER_AUTH_LOGIN: '/api/v1/customers/{customerId}/auth/login',
  CUSTOMER_AUTH_STATUS: '/api/v1/customers/{customerId}/auth/status',
  CUSTOMER_AUTH_MFA_SETUP: '/api/v1/customers/{customerId}/auth/mfa/setup',
  CUSTOMER_ME: '/api/v1/customers/{customerId}/me',

  // Customer-specific resource routes
  CUSTOMER_USERS: '/api/v1/customers/{customerId}/users',
  CUSTOMER_GROUPS: '/api/v1/customers/{customerId}/groups',
  CUSTOMER_GROUP_MEMBERS: '/api/v1/customers/{customerId}/groups/{groupName}/members',

  // Customer-specific SSO replication endpoints
  CUSTOMER_SSO_CONFIG: '/api/v1/customers/{customerId}/sso-replication/sso-config',
  CUSTOMER_SSO_REPLICATE_USERS: '/api/v1/customers/{customerId}/sso-replication/users',
  CUSTOMER_SSO_REPLICATE_FULL: '/api/v1/customers/{customerId}/sso-replication/full',
  CUSTOMER_ONBOARD_SSO_ACCOUNTS: '/api/v1/customers/{customerId}/accounts/onboard/sso-accounts',

  // Customer-specific SCIM endpoints
  CUSTOMER_SCIM_GENERATE_TOKEN: '/api/v1/customers/{customerId}/scim/tokens/generate',
  CUSTOMER_SCIM_LIST_TOKENS: '/api/v1/customers/{customerId}/scim/tokens/list',
  CUSTOMER_SCIM_REVOKE_TOKEN: '/api/v1/customers/{customerId}/scim/tokens/revoke',
  CUSTOMER_SCIM_DELETE_TOKEN: '/api/v1/customers/{customerId}/scim/tokens/delete',

  // JIT Portal endpoints
  JIT_SSO_CALLBACK: '/sso-callback',
  JIT_REQUESTS: '/api/v1/jit/requests',
  JIT_REQUEST_DETAIL: '/api/v1/jit/requests/{id}',
  JIT_SESSIONS_ACTIVE: '/api/v1/jit/sessions/active',
  JIT_SESSIONS_HISTORY: '/api/v1/jit/sessions/history',
  JIT_SESSION_REVOKE: '/api/v1/jit/sessions/{id}/revoke',
  JIT_ACCOUNTS: '/api/v1/jit/accounts',
  JIT_PERMISSION_SETS: '/api/v1/jit/permission-sets',
  JIT_ME: '/api/v1/jit/me',
  JIT_APPROVALS_PENDING: '/api/v1/jit/approvals/pending',
  JIT_APPROVALS_HISTORY: '/api/v1/jit/approvals/history',
  JIT_APPROVE: '/api/v1/jit/approvals/{id}/approve',
  JIT_REJECT: '/api/v1/jit/approvals/{id}/reject',
  JIT_APPROVALS_SESSIONS_ACTIVE: '/api/v1/jit/approvals/sessions/active',
  JIT_ACCOUNTS_OWNED: '/api/v1/jit/accounts/owned',
  JIT_ACCOUNTS_OWNED_ASSIGNMENTS: '/api/v1/jit/accounts/owned/assignments',
  JIT_AUTH_REFRESH: '/api/v1/jit/auth/refresh',

  // Multi-level approval endpoints (for custom permission sets)
  JIT_OWNER_APPROVE: '/api/v1/jit/approvals/{id}/owner-approve',
  JIT_OWNER_REJECT: '/api/v1/jit/approvals/{id}/owner-reject',
  JIT_ADMIN_APPROVE: '/api/v1/jit/approvals/{id}/admin-approve',
  JIT_ADMIN_REJECT: '/api/v1/jit/approvals/{id}/admin-reject',
  JIT_ADMIN_APPROVALS_PENDING: '/api/v1/jit/approvals/admin/pending',

  // AWS policy helpers for custom permission set builder
  JIT_AWS_MANAGED_POLICIES: '/api/v1/jit/aws/managed-policies',
  JIT_AWS_VALIDATE_POLICY: '/api/v1/jit/aws/validate-policy',

  // Customer-scoped JIT admin endpoints
  CUSTOMER_JIT_CONFIG: '/api/v1/customers/{customerId}/jit/config',
  CUSTOMER_JIT_REQUESTS: '/api/v1/customers/{customerId}/jit/requests',
  CUSTOMER_JIT_SESSIONS: '/api/v1/customers/{customerId}/jit/sessions',
};

export const MESSAGES = {
  // Authentication messages
  ADMIN_INITIALIZED: 'Admin user initialized successfully. Please complete MFA setup.',
  LOGIN_SUCCESS: 'Login successful',
  LOGIN_FAILED: 'Login failed',
  MFA_SETUP_REQUIRED: 'Multi-factor authentication setup is required',
  MFA_SETUP_COMPLETE: 'MFA setup completed successfully',
  MFA_CODE_INVALID: 'Invalid TOTP code. Please try again.',
  MFA_DISABLED: 'Multi-factor authentication has been disabled',
  PASSWORD_CHANGED: 'Password changed successfully',
  AUTH_TOKEN_EXPIRED: 'Session expired - please login again',
  AUTH_REQUIRED: 'Authentication required - please login',
  LOGOUT_SUCCESS: 'Logged out successfully',

  // Customer MFA messages
  CUSTOMER_MFA_SETUP_REQUIRED: 'MFA setup is mandatory for customer admin',
  CUSTOMER_MFA_SETUP_COMPLETE: 'Customer MFA setup completed successfully',
  CUSTOMER_MFA_CODE_INVALID: 'Invalid customer MFA code. Please try again.',

  // User and group messages
  USER_CREATED: 'User created successfully',
  USER_CREATED_WITH_OFFLINE_ACCESS: 'User created successfully with offline access enabled',
  USER_UPDATED: 'User updated successfully',
  USER_DELETED: 'User deleted successfully',
  GROUP_CREATED: 'Group created successfully',
  GROUP_DELETED: 'Group deleted successfully',
  USERS_ADDED_TO_GROUP: 'Users assigned to group successfully',
  USERS_REMOVED_FROM_GROUP: 'Users removed from group successfully',

  // Permission set messages
  PERMISSION_SET_CREATED: 'Permission set created successfully',
  PERMISSION_SET_UPDATED: 'Permission set updated successfully',
  PERMISSION_SET_DELETED: 'Permission set deleted successfully',

  // Permission set assignment messages
  PERMISSION_SET_ASSIGNED: 'Permission set assigned successfully',
  PERMISSION_SET_ASSIGNED_TO_GROUP: 'Permission set assigned to group successfully',
  PERMISSION_SET_ASSIGNMENT_DELETED: 'Permission set assignment removed successfully',

  // SSO Replication messages
  SSO_USERS_REPLICATED: 'Users replicated to Keycloak successfully',
  SSO_USERS_DRY_RUN_COMPLETED: 'Dry run completed - preview of user replication',
  SSO_USER_REPLICATION_ERROR: 'Failed to replicate users to Keycloak',

  // Init messages
  MANAGEMENT_ACCOUNT_INITIALIZED: 'Management account initialized successfully',
  MANAGEMENT_ACCOUNT_UPDATED: 'Management account updated successfully',
  MANAGEMENT_ACCOUNT_DELETED: 'Management account deleted successfully',
  MANAGEMENT_ACCOUNT_FETCHED: 'Management account configuration retrieved successfully',

  // Keycloak initialization messages
  KEYCLOAK_INITIALIZED: 'Keycloak initialized successfully',
  KEYCLOAK_STATUS_FETCHED: 'Keycloak status retrieved successfully',
  KEYCLOAK_VALIDATED: 'Keycloak configuration validated successfully',

  // SCIM messages
  SCIM_TOKEN_GENERATED: 'SCIM token generated successfully',
  SCIM_TOKEN_REVOKED: 'SCIM token revoked successfully',
  SCIM_TOKEN_DELETED: 'SCIM token deleted successfully',
  SCIM_TOKENS_LISTED: 'SCIM tokens retrieved successfully',
  SCIM_TOKEN_GENERATE_ERROR: 'Failed to generate SCIM token',
  SCIM_TOKEN_REVOKE_ERROR: 'Failed to revoke SCIM token',
  SCIM_TOKEN_DELETE_ERROR: 'Failed to delete SCIM token',
  SCIM_TOKENS_LIST_ERROR: 'Failed to retrieve SCIM tokens',

  // Error messages
  FETCH_USERS_ERROR: 'Failed to fetch users',
  FETCH_GROUPS_ERROR: 'Failed to fetch groups',
  CREATE_USER_ERROR: 'Failed to create user',
  CREATE_GROUP_ERROR: 'Failed to create group',
  UPDATE_USER_ERROR: 'Failed to update user',
  DELETE_USER_ERROR: 'Failed to delete user',
  DELETE_GROUP_ERROR: 'Failed to delete group',
  FETCH_USER_DETAILS_ERROR: 'Failed to fetch user details',
  FETCH_GROUP_DETAILS_ERROR: 'Failed to fetch group details',
  FETCH_GROUP_MEMBERS_ERROR: 'Failed to fetch group members',
  ADD_USERS_TO_GROUP_ERROR: 'Failed to assign users to group',
  REMOVE_USERS_FROM_GROUP_ERROR: 'Failed to remove users from group',

  // Permission set error messages
  FETCH_PERMISSION_SETS_ERROR: 'Failed to fetch permission sets',
  FETCH_PERMISSION_SET_DETAILS_ERROR: 'Failed to fetch permission set details',
  CREATE_PERMISSION_SET_ERROR: 'Failed to create permission set',
  UPDATE_PERMISSION_SET_ERROR: 'Failed to update permission set',
  DELETE_PERMISSION_SET_ERROR: 'Failed to delete permission set',
  FETCH_AWS_MANAGED_POLICIES_ERROR: 'Failed to fetch AWS managed policies',
  FETCH_AWS_MANAGED_POLICY_ERROR: 'Failed to fetch AWS managed policy details',

  // New error messages
  FETCH_ACCOUNTS_ERROR: 'Failed to fetch AWS accounts',
  FETCH_PERMISSION_SET_ASSIGNMENTS_ERROR: 'Failed to fetch permission set assignments',
  FETCH_PERMISSION_SET_ASSIGNMENT_ERROR: 'Failed to fetch permission set assignment details',
  CREATE_PERMISSION_SET_ASSIGNMENT_ERROR: 'Failed to create permission set assignment',
  DELETE_PERMISSION_SET_ASSIGNMENT_ERROR: 'Failed to delete permission set assignment',

  // Init error messages
  INIT_MANAGEMENT_ACCOUNT_ERROR: 'Failed to initialize management account',
  FETCH_MANAGEMENT_ACCOUNT_ERROR: 'Failed to fetch management account configuration',
  DELETE_MANAGEMENT_ACCOUNT_ERROR: 'Failed to delete management account configuration',

  // Keycloak initialization error messages
  INIT_KEYCLOAK_ERROR: 'Failed to initialize Keycloak configuration',
  FETCH_KEYCLOAK_STATUS_ERROR: 'Failed to fetch Keycloak status',
  VALIDATE_KEYCLOAK_ERROR: 'Failed to validate Keycloak configuration',

  // Status codes
  STATUS_400: 'Invalid request data',
  STATUS_404: 'Resource not found',
  STATUS_500: 'Server error occurred',

  // Customer management messages
  CREATE_CUSTOMER_SUCCESS: "Customer created successfully",
  CREATE_CUSTOMER_ERROR: "Failed to create customer",
  DELETE_CUSTOMER_SUCCESS: "Customer deleted successfully",
  DELETE_CUSTOMER_ERROR: "Failed to delete customer",
  DELETE_CUSTOMER_PARTIAL: "Customer deleted with some errors - see details",
  GET_CUSTOMER_RESOURCE_COUNTS_ERROR: "Failed to get customer resource counts",
};